import Promise from 'bluebird';
import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import { SubmissionError, reset, change, destroy, getFormValues } from 'redux-form';

import qs from 'qs';
import moment from 'moment';

import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import flatten from 'lodash/flatten';
import _pathOr from 'lodash/fp/pathOr';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import { actions as rootActions } from '../../root';

import createThreadForm from '../../threads/components/create-form';
import createPayoutForm from '../../payouts/components/forms/create-payout';
import createApproveDailyForm from '../components/approve-daily-form';
import creteAddOfferFromAffiliateForm from '../components/add-offer-from-affiliate-form';
import createTransactionForm from '../../payouts/components/forms/transaction';
import createPointTransactionForm from '../components/points-transaction-form';
import createIncreaseApproveForm from '../components/increase-approve-form';
import createRequestGarantForm from '../components/request-garant-form';
import createIndividualTerms from '../components/profile/forms/individual-terms';
import createAttachments from '../components/profile/forms/attachments';
import createChildAffiliatesForm from '../components/profile/affiliates';
import createTransferBalanceAndAccumulatedPointsForm
	from '../components/create-transfer-balance-and-accumulated-points-form';
import createIndividualAlertsTerms from '../components/profile/forms/alerts';
import createDashboardVisits from '../components/profile/dashboard-visits';

import createSwitchControlMulti from '../../shared/switch-control/multi';

import checkPermissions from '../../shared/check-permissions';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import createFilters from '../../shared/filters';
import { enum as affiliateStatusesEnum } from '../../../../../../enums/affiliate-statuses';
import {createSelectOptionsFromSysEnums} from '../../shared/form-elements/select';

const isRocket = process.env.CPA_ENV === 'rocket';

const threadForm = createThreadForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'createThreadForm',
	formName: 'affiliateProfileCreateThreadForm'
});

const payoutForm = createPayoutForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'createPayoutForm',
	formName: 'affiliateProfileCreatePayoutForm'
});

const approveDailyForm = createApproveDailyForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'approveDailyForm',
	formName: 'approveDailyForm'
});

const transactionForm = createTransactionForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'transactionForm',
	formName: 'affiliateProfileTransactionForm'
});

const addOfferFromAffiliateForm = creteAddOfferFromAffiliateForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'addOfferFromAffiliateForm',
	formName: 'addOfferFromAffiliateForm'
});

const pointTransactionForm = createPointTransactionForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'pointTransactionForm',
	formName: 'affiliateProfilePointTransactionForm'
});

const increaseApproveForm = createIncreaseApproveForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'increaseApproveForm',
	formName: 'increaseApproveForm'
});

const requestGarantForm = createRequestGarantForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'requestGarantForm',
	formName: 'requestGarantForm'
});

const individualTermsForm = createIndividualTerms({
	namespace: 'affiliateProfile',
	stateNodeName: 'individualTermsForm'
});
const individualTermsFilters = createFilters({
	namespace: 'affiliateProfile',
	stateNodeName: 'individualTermsFilters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'country_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: !isRocket,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS
			}
		}
	]
});

const childAffiliatesForm = createChildAffiliatesForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'childAffiliatesForm'
});

const individualAlertsForm = createIndividualAlertsTerms({
	namespace: 'affiliateProfile',
	stateNodeName: 'individualAlertsForm'
});

const transferBalanceAndAccumulatedPointsForm = createTransferBalanceAndAccumulatedPointsForm({
	namespace: 'affiliateProfile',
	stateNodeName: 'transferBalanceAndAccumulatedPointsForm',
	formName: 'transferBalanceAndAccumulatedPointsForm'
});

const dashboardVisits = createDashboardVisits({
	namespace: 'affiliateProfile',
	stateNodeName: 'dashboardVisits'
});

const calendar = createCalendar({
	namespace: 'affiliateProfile',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment()
			.subtract(isRocket ? 6 : 2, 'day')
			.format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const affiliateApproveIncreaseCalendar = createCalendar({
	namespace: 'affiliateProfile',
	stateNodeName: 'affiliateApproveIncreaseCalendar',
	defaultValue: {
		start: moment().format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const affiliateTransactionCalendar = createCalendar({
	namespace: 'affiliateProfile',
	stateNodeName: 'affiliateTransactionCalendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const garantRequestCalendar = createCalendar({
	namespace: 'affiliateProfile',
	stateNodeName: 'garantRequestCalendar',
	defaultValue: {
		start: moment().format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const dashboardVisitCalendar = createCalendar({
	namespace: 'affiliateProfile',
	stateNodeName: 'dashboardVisitCalendar',
	defaultValue: {
		start: moment()
			.subtract(7, 'day')
			.format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const attachments = createAttachments({
	namespace: 'affiliateProfile',
	stateNodeName: 'attachment'
});

const dashboardVisitActionsSwitch = createSwitchControlMulti({
	namespace: 'affiliateProfile',
	stateNodeName: 'dashboardVisitActionsSwitch',
	defaultValues: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'signup', placeholder: 'signup', active: false },
		{ name: 'login', placeholder: 'login', active: false },
		{ name: 'signin', placeholder: 'signin', active: false }
	]
});

// actions types
const APPLY_FILTERS = 'affiliate-profile/apply-filters';

const FETCH_DATA_SUCCESS = 'affiliate-profile/fetch-data-success';
const FETCH_DATA_ERROR = 'affiliate-profile/fetch-data-error';

const UPDATE_DATA_SUCCESS = 'affiliate-profile/update-data-success';

const FETCH_OFFERS_SUCCESS = 'affiliate-profile/fetch-offers-success';
const FETCH_MATRIX_SUCCESS = 'affiliate-profile/fetch-matrix-success';
const FETCH_MATRIX_ERROR = 'affiliate-profile/fetch-matrix-err';
const FETCH_ALERTS_SUCCESS = 'affiliate-profile/fetch-alerts-success';
const FETCH_PAYOUT_SETTINGS_SUCCESS = 'affiliate-profile/fetch-payout-settings-success';
const FETCH_PAYOUT_SYSTEMS_SUCCESS = 'affiliate-profile/fetch-payout-systems-success';
const FETCH_CHILD_AFFILIATES_SUCCESS = 'affiliate-profile/fetch-child-affiliates-success';
const FETCH_DASHBOARD_SIGNUP_VISIT_DATA_SUCCESS = 'affiliate-profile/fetch-dashboard-signup-visit-data-success';

const SESSIONS_DELETE_SUCCESS = 'affiliate-profile/session-delete-success';

const LOGIN_WITH_AFFILIATE_SUCCESS = 'affiliate-profile/login-with-affiliate-success';

const SHOW_MORE_LOGS_SUCCESS = 'affiliate-profile/show-more-logs-success';
const REFETCH_LOGS_SUCCESS = 'affiliate-profile/refetch-logs-success';

const FETCH_NOTES_SUCCESS = 'affiliate-profile/fetch-notes-success';
const FETCH_NOTES_ERROR = 'affiliate-profile/fetch-notes-error';

const SHOW_MORE_NOTES_SUCCESS = 'affiliate-profile/show-more-notes-success';

const COMMENT_SUCCESS = 'affiliate-profile/comment-success';
const COMMENT_ERROR = 'affiliate-profile/comment-error';

const NOTE_REMOVE_SUCCESS = 'affiliate-profile/comment-remove-success';

const TOGGLE_AFFILIATE_STATUS = 'affiliate-profile/toggle-status';
const TOGGLE_AFFILIATE_LEVEL = 'affiliate-profile/toggle-level';

const TOGGLE_PROGRESS_BAR = 'affiliate-profile/toggle-progress-bar';
const SHOW_COMMENT_SUBMIT_BTN = 'affiliate-profile/show-comment-submit-btn';

const CHANGE_TAB = 'affiliate-profile/change-tab';
const REQUEST_ERROR = 'affiliate-profile/request-error';

const RESET_CALENDAR = 'affiliate-profile/reset-calendar';
const RESET_IND_TERMS_FORM = 'affiliate-profile/reset-ind-terms-form';
const RESET_CHILD_AFFS_FORM = 'affiliate-profile/reset-child-affs-form';
const RESET_IND_ALERTS_FORM = 'affiliate-profile/reset-ind-alerts-terms-form';

const MOVE_MONEY_SUCCESS = 'affiliate-profile/move-money-success';
const MOVE_MONEY_ERROR = 'affiliate-profile/move-balance-to-hold-error';

const UPDATE_GEOS = 'affiliate-profile/update-geos';

// permissions
export const allowedPermissions = {
	view: 'affiliates_list',
	view_statistics: 'analytics_view',
	view_threads: 'threads_list',
	edit: 'affiliates_update',
	login: 'affiliates_login',
	changeLevel: 'affiliates_update',
	payoutsCreate: 'payouts_create',
	viewPayoutSystems: 'payout_systems_list',
	createTransaction: 'transactions_create',
	conversionsManualApprove: 'conversions_manual_approve',
	conversionsPastManualApprove: 'conversions_past_manual_approve',
	garantApproveRequestsView: 'garant_approve_requests_view',
	conversionsApproveDaily: 'conversions_approve_daily',
	createThreads: 'threads_create',
	offersGrant: 'offers_grant',
	viewOffers: 'offers_list',
	moveBalance: 'payouts_approve',
	transferPoints: 'transfer_points',
	offersChangeCap: 'offers_change_cap',
	transferBalanceAndAccumulatedPoints: 'transfer_balance_and_accumulated_points',
	sessionsDelete: 'sessions_delete'
};

// initial state
const initialState = {
	data: null,
	logsPagination: {
		currentPage: 1,
		perPage: 10,
		total: null
	},
	notesPagination: {
		currentPage: 1,
		perPage: 10,
		total: null
	},
	search: null,
	params: {
		tickets: true,
		payouts: true,
		comments: true,
		offers: false,
		campaigns: false,
		edits: true,
		individual_terms: true,
		emails: false,
		other: false
	},
	offers: null,
	payoutMatrix: null,
	individualTerms: null,
	payoutSettings: null,
	notes: null,
	balances: null,
	childAffiliates: null,
	childAffiliatesAmount: 0, // for pagination
	totalChildAffiliatesAmount: 0, // for display in tab
	calendar: calendar.initialState,
	affiliateApproveIncreaseCalendar: affiliateApproveIncreaseCalendar.initialState,
	affiliateTransactionCalendar: affiliateTransactionCalendar.initialState,
	garantRequestCalendar: garantRequestCalendar.initialState,
	dashboardVisitCalendar: dashboardVisitCalendar.initialState,
	createThreadForm: threadForm.initialState,
	createPayoutForm: payoutForm.initialState,
	transactionForm: transactionForm.initialState,
	addOfferFromAffiliateForm: addOfferFromAffiliateForm.initialState,
	approveDailyForm: approveDailyForm.initialState,
	transferBalanceAndAccumulatedPointsForm: transferBalanceAndAccumulatedPointsForm.initialState,
	pointTransactionForm: pointTransactionForm.initialState,
	increaseApproveForm: increaseApproveForm.initialState,
	requestGarantForm: requestGarantForm.initialState,
	individualTermsForm: individualTermsForm.initialState,
	individualTermsFilters: individualTermsFilters.initialState,
	individualAlertsForm: individualAlertsForm.initialState,
	attachment: attachments.initialState,
	childAffiliatesForm: childAffiliatesForm.initialState,
	dashboardVisits: dashboardVisits.initialState,
	dashboardSignupVisitData: null,
	dashboardVisitActionsSwitch: dashboardVisitActionsSwitch.initialState,
	isCreatePayoutFormOpen: false,
	payoutSystems: null,
	affiliateId: null,
	currentTab: 'timeline',
	showProgressBar: false,
	showCommentSubmitBtn: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { perPage } = getState().affiliateProfile.logsPagination;
			request(0, perPage, props, dispatch, getState).then(() => { dispatch(actions.apply()); });
		},

		loginWithAffiliate: id => dispatch => {
			const isNewDashboard = isRocket && confirm('Перейти в новый дашборд?');

			const pageData = {
				pathname: window.location.pathname,
				title: 'Affiliate profile'
			};

			const request = http({
				url: endpoints.AFFILIATES.LOGIN.replace('{id}', id),
				method: 'post',
				actions: [LOGIN_WITH_AFFILIATE_SUCCESS, REQUEST_ERROR],
				data: {
					isNewDashboard,
					source_page: pageData
				}
			});
			request(dispatch).then(action => {
				if (action.type === LOGIN_WITH_AFFILIATE_SUCCESS) {
					window.location.replace(action.payload.data.url);
				}
			});
		},

		submitDescription: data => (dispatch, getState) => {
			const { affiliateId } = getState().affiliateProfile;

			const request = http({
				url: `${endpoints.AFFILIATES.BASE}/${affiliateId}`,
				method: 'put',
				data: {
					description: data.description || null
				},
				actions: [UPDATE_DATA_SUCCESS, REQUEST_ERROR]
			});

			return request(dispatch);
		},

		toggleStatus: (id, currentStatus) => dispatch => {
			if (!isRocket) {
				const newStatus =
					currentStatus === affiliateStatusesEnum.active ? affiliateStatusesEnum.banned : affiliateStatusesEnum.active;

				confirm(`Confirm changing status to "${newStatus}".`) &&
				http({
					url: `${endpoints.AFFILIATES.BASE}/${id}`,
					method: 'put',
					actions: [TOGGLE_AFFILIATE_STATUS, REQUEST_ERROR],
					data: {
						status: newStatus
					},
					addToResponse: newStatus
				})(dispatch);
			} else {
				const level = currentStatus;
				let newLevel;

				if (level !== 'average') {
					if (confirm('Вы действительно хотите активировать аккаунт?')) {
						newLevel = 'average';
					}
				} else {
					if (confirm('Вы действительно хотите забанить аккаунт?')) {
						newLevel = 'banned';
					}
				}

				if (newLevel) {
					http({
						url: `${endpoints.AFFILIATES.BASE}/${id}`,
						method: 'put',
						actions: [TOGGLE_AFFILIATE_LEVEL, REQUEST_ERROR],
						data: {
							level: newLevel
						},
						addToResponse: newLevel
					})(dispatch);
				}
			}
		},

		moveMoney: (id, currency, direction) => (dispatch, getState) => {
			const translator = intl(getState());
			const confirmText =
				direction === 'move_to_hold' ? translator('Move balance to hold?') : translator('Move hold to balance?');
			const endpointFragment = direction === 'move_to_hold' ? 'on-hold' : 'from-hold';

			if (!confirm(confirmText)) return;

			const request = http({
				url: `${endpoints.AFFILIATES.BASE}/${id}/${endpointFragment}`,
				method: 'put',
				data: { currency },
				actions: [MOVE_MONEY_SUCCESS, MOVE_MONEY_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === MOVE_MONEY_SUCCESS) {
					dispatch(actions.toggleSpinner());
					return dispatch(actions.refetchLogs()).then(action => {
						if (action.type === REFETCH_LOGS_SUCCESS) {
							dispatch(actions.toggleSpinner());
						}
						return action;
					});
				}
			});
		},

		changeTab: tab => (dispatch, getState) => {
			const { affiliateId, currentTab } = getState().affiliateProfile;

			if (tab === 'individual-terms') {
				dispatch(actions.toggleSpinner());
				dispatch(actions.fetchOffers(affiliateId)).then(action => {
					if (action.type === FETCH_OFFERS_SUCCESS) {
						dispatch(actions.resetIndTermsForm());
						dispatch(actions.toggleSpinner());
					}
				});
			}

			if (tab === 'individual-alerts') {
				dispatch(actions.toggleSpinner());
				dispatch(actions.fetchAlerts(affiliateId)).then(action => {
					if (action.type === FETCH_ALERTS_SUCCESS) {
						dispatch(actions.resetIndAlertsForm());
						dispatch(actions.toggleSpinner());
					}
				});
			}

			if (tab === 'payout-settings') {
				dispatch(actions.toggleSpinner());
				dispatch(actions.fetchPayoutSystems()).then(action => {
					if (action.type === FETCH_PAYOUT_SYSTEMS_SUCCESS) {
						dispatch(actions.fetchPayoutSettings(affiliateId)).then(action => {
							if (action.type === FETCH_PAYOUT_SETTINGS_SUCCESS) {
								dispatch(actions.toggleSpinner());
							}
						});
					}
				});
			}

			if (tab === 'timeline' && currentTab !== 'timeline') {
				dispatch(actions.toggleSpinner());
				dispatch(actions.refetchLogs()).then(action => {
					if (action.type === REFETCH_LOGS_SUCCESS) {
						dispatch(actions.toggleSpinner());
					}
				});
			}

			if (tab === 'affiliates' && currentTab !== 'affiliates') {
				dispatch(actions.toggleSpinner());
				dispatch(actions.resetChildAffiliatesForm());
				Promise.all([
					dispatch(actions.fetchOffers(affiliateId)),
					dispatch(actions.fetchChildAffiliates(affiliateId))
				]).spread((offersAction, childAffAction) => {
					if (offersAction.type === FETCH_OFFERS_SUCCESS && childAffAction.type === FETCH_CHILD_AFFILIATES_SUCCESS) {
						dispatch(actions.toggleSpinner());
					}
				});
			}

			// destroy affs form after leaving
			if (currentTab === 'affiliates' && tab !== 'affiliates') {
				dispatch(actions.resetChildAffiliatesForm());
				dispatch(destroy('childAffiliatesForm'));
			}

			if (tab === 'dashboard-visits' && currentTab !== 'dashboard-visits') {
				dispatch(actions.fetchDashboardSignupVisitData());
				dispatch(actions.fetchDataDashboardVisits());
			}

			if (tab === 'notes') {
				dispatch(actions.toggleSpinner());
				dispatch(actions.fetchNotes()).then(action => {
					if (action.type === FETCH_NOTES_SUCCESS) {
						dispatch(actions.toggleSpinner());
					}
				});
			}

			dispatch({
				type: CHANGE_TAB,
				payload: tab
			});
		},

		fetchDashboardSignupVisitData: () => (dispatch, getState) => {
			const { affiliateId } = getState().affiliateProfile;

			return http({
				url: `${endpoints.DASHBOARD_VISITS.BY_AFFILIATE.replace('{id}', affiliateId)}/signup-data`,
				method: 'get',
				actions: [FETCH_DASHBOARD_SIGNUP_VISIT_DATA_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		fetchDataDashboardVisits: (fetch = dashboardVisits.actions.fetchData) => (dispatch, getState) => {
			const { affiliateId, dashboardVisitActionsSwitch, dashboardVisitCalendar } = getState().affiliateProfile;

			const filters = {
				actions: dashboardVisitActionsSwitch.filters.filter(f => f.active && f.name !== 'all').map(f => f.name)
			};

			dispatch(actions.toggleSpinner());
			dispatch(fetch(affiliateId, filters, getDateRangeFromCalendar(dashboardVisitCalendar)))
				.then(() => dispatch(actions.toggleSpinner()));
		},

		showMoreDashboardVisits: () => (dispatch, getState) => {
			dispatch(actions.fetchDataDashboardVisits(dashboardVisits.actions.fetchMore));
		},

		switchDashboardVisitsActions: dashboardVisitActionsSwitch.actions.switchControlMultiChooseFilter,

		locationsLookup: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
				method: 'get',
				actions: [
					'FETCH_LOCATIONS_SUCCESS/FETCH_LOCATIONS_SUCCESS',
					'FETCH_LOCATIONS_ERROR/FETCH_LOCATIONS_ERROR'
				]
			});

			return request(dispatch).then(action => {
				if (action.type === 'FETCH_LOCATIONS_SUCCESS/FETCH_LOCATIONS_SUCCESS') {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		fetchOffers: id => dispatch => {
			return http({
				url: `${endpoints.OFFERS.BASE}?affiliate_id=${id}`,
				method: 'get',
				actions: [FETCH_OFFERS_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch)
				.then(async action => {
					if (process.env.CPA_ENV === 'everad') {
						await http({
							url: `${endpoints.PAYOUT_REQUESTS.BASE}/matrix?${qs.stringify({is_simple_result: true})}`,
							method: 'get',
							actions: [FETCH_MATRIX_SUCCESS, FETCH_MATRIX_ERROR]
						})(dispatch);
					}
					return action;
				});
		},

		fetchAlerts: id => dispatch => {
			return http({
				url: `${endpoints.ALERTS.BASE}?affiliate_id=${id}`,
				method: 'get',
				actions: [FETCH_ALERTS_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		fetchChildAffiliates: (id, fetchOptions = {}, options = {}) => (dispatch, getState) => {
			const initialQuery = getState().affiliateProfile.childAffiliatesForm.query;
			const offset = options.offset || initialQuery.offset;
			const limit = options.limit || initialQuery.limit;
			const { shouldShowProgressBar = false, shouldAppendData = false } = fetchOptions;

			return http({
				url: `${endpoints.AFFILIATES.MASTER_ACCOUNT}/${id}?${qs.stringify({ ...options, offset, limit })}`,
				method: 'get',
				actions: [FETCH_CHILD_AFFILIATES_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: {
					shouldShowProgressBar,
					shouldAppendData
				}
			})(dispatch);
		},

		fetchPayoutSettings: id => dispatch => {
			dispatch(actions.resetPayoutsForm());
			return http({
				url: endpoints.AFFILIATES.PAYOUT_SETTINGS.replace('{id}', id),
				method: 'get',
				actions: [FETCH_PAYOUT_SETTINGS_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		fetchPayoutSystems: () => dispatch => {
			return http({
				url: endpoints.PAYOUT_SYSTEMS.LIST,
				method: 'get',
				actions: [FETCH_PAYOUT_SYSTEMS_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		showMoreLogs: () => (dispatch, getState) => {
			const { logsPagination, affiliateId, calendar } = getState().affiliateProfile;
			const { perPage, currentPage } = logsPagination;
			const offset = currentPage * perPage;
			const page = currentPage + 1;
			const paramsFormValues = getFormValues('affiliateProfileParamsForm')(getState());
			const params = paramsFormValues &&
				Object.keys(paramsFormValues).filter(key => !!paramsFormValues[key]);

			const searchFormValues = getFormValues('affiliateProfileSearchForm')(getState());
			const search = searchFormValues ? searchFormValues.search : null;
			const query = qs.stringify({
				offset,
				params: params && params.length !== 0 ? params: Object.keys(initialState.params),
				search,
				limit: perPage,
				date_range: getDateRangeFromCalendar(calendar)
			});

			dispatch(actions.toggleSpinner());

			const request = http({
				url: `${endpoints.AFFILIATES.LOGS.replace('{id}', affiliateId)}?${query}`,
				method: 'get',
				actions: [SHOW_MORE_LOGS_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: page
			});

			return request(dispatch).then(action => {
				if (action.type === SHOW_MORE_LOGS_SUCCESS) {
					dispatch(actions.toggleSpinner());
				}
			});
		},

		toggleSpinner: () => dispatch => dispatch(rootActions.spinnerToggle()),
		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		refetchLogs: () => (dispatch, getState) => {
			const state = getState();
			const { affiliateId, logsPagination, calendar } = state.affiliateProfile;
			const { perPage } = logsPagination;

			const searchFormValues = getFormValues('affiliateProfileSearchForm')(state);
			const search = searchFormValues ? searchFormValues.search : null;

			const paramsFormValues = getFormValues('affiliateProfileParamsForm')(state) || initialState.params;
			const params = paramsFormValues && Object.keys(paramsFormValues).filter(key => !!paramsFormValues[key]);
			const query = qs.stringify({
				offset: 0,
				limit: perPage,
				search,
				params: params && params.length !== 0 ? params: Object.keys(initialState.params),
				date_range: getDateRangeFromCalendar(calendar)
			});

			const refetchLogsRequest = http({
				url: `${endpoints.AFFILIATES.LOGS.replace('{id}', affiliateId)}?${query}`,
				method: 'get',
				actions: [REFETCH_LOGS_SUCCESS, FETCH_DATA_ERROR]
			});

			return refetchLogsRequest(dispatch);
		},

		fetchNotes: () => (dispatch, getState) => {
			const state = getState();
			const { affiliateId, notesPagination } = state.affiliateProfile;
			const { perPage } = notesPagination;
			const query = qs.stringify({offset: 0, limit: perPage});

			const request = http({
				url: `${endpoints.AFFILIATES.NOTES.replace('{id}', affiliateId)}?${query}`,
				method: 'get',
				actions: [FETCH_NOTES_SUCCESS, FETCH_NOTES_ERROR]
			});

			return request(dispatch);
		},

		showMoreNotes: () => (dispatch, getState) => {
			const state = getState();
			const { affiliateId, notesPagination } = state.affiliateProfile;
			const { perPage, currentPage } = notesPagination;
			const offset = currentPage * perPage;
			const page = currentPage + 1;
			const query = qs.stringify({offset: offset, limit: perPage});

			const request = http({
				url: `${endpoints.AFFILIATES.NOTES.replace('{id}', affiliateId)}?${query}`,
				method: 'get',
				actions: [ SHOW_MORE_NOTES_SUCCESS ],
				addToResponse: page
			});

			return request(dispatch);
		},

		submitNote: data => (dispatch, getState) => {
			const state = getState();
			const { affiliateId } = state.affiliateProfile;

			dispatch(actions.toggleSpinner());
			const commentId = affiliateId + Date.now();

			const request = http({
				url: `${endpoints.AFFILIATES.LOGS.replace('{id}', affiliateId)}/comment`,
				method: 'post',
				data: { comment: data.comment || '', action: 'admin_note', metadata: {id: commentId} },
				actions: [ COMMENT_SUCCESS, COMMENT_ERROR ]
			});

			return request(dispatch).then(action => {
				dispatch(actions.toggleSpinner());
				if (action.type === COMMENT_SUCCESS) {
					dispatch(reset('affiliateProfileCommentForm'));
					dispatch(actions.fetchNotes());
				}
			});
		},

		removeNote: commentId => (dispatch, getState) => {

			dispatch(actions.toggleSpinner());

			const request = http({
				url: `v2/affiliates/{id}/note`.replace('{id}', commentId),
				method: 'delete',
				actions: [ NOTE_REMOVE_SUCCESS ]
			});

			return request(dispatch).then(action => {
				dispatch(actions.toggleSpinner());
				if (action.type === NOTE_REMOVE_SUCCESS) {
					dispatch(actions.fetchNotes());
				}
			});
		},

		submitComment: data => (dispatch, getState) => {
			const state = getState();
			const { affiliateId } = state.affiliateProfile;

			dispatch(actions.toggleSpinner());

			const request = http({
				url: `${endpoints.AFFILIATES.LOGS.replace('{id}', affiliateId)}/comment`,
				method: 'post',
				data: { comment: data.comment || '' },
				actions: [COMMENT_SUCCESS, COMMENT_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === COMMENT_SUCCESS) {
					dispatch(actions.resetCalendar());
					dispatch(reset('affiliateProfileSearchForm'));
					dispatch(reset('affiliateProfileParamsForm'));
					return dispatch(actions.refetchLogs()).then(action => {
						if (action.type === REFETCH_LOGS_SUCCESS) {
							dispatch(actions.toggleSpinner());
						}
						return action;
					});
				}
				if (action.type === COMMENT_ERROR && action.payload.response.status === 400) {
					dispatch(actions.toggleSpinner());
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
		},

		setShowCommentSubmitBtn: () => ({
			type: SHOW_COMMENT_SUBMIT_BTN
		}),

		apply: () => (dispatch, getState) => {
			dispatch(actions.toggleSpinner());

			return dispatch(actions.refetchLogs()).then(action => {
				if (action.type === REFETCH_LOGS_SUCCESS) {
					const displayedList = action.payload.data.rows;

					dispatch({
						type: APPLY_FILTERS,
						payload: displayedList
					});
					dispatch(actions.toggleSpinner());
				}
				return action;
			});
		},

		clear: () => dispatch => {
			dispatch(actions.toggleSpinner());
			dispatch(actions.resetCalendar());
			dispatch(reset('affiliateProfileSearchForm'));
			dispatch(reset('affiliateProfileParamsForm'));
			dispatch(reset('affiliateProfileCommentForm'));
			return dispatch(actions.refetchLogs()).then(action => {
				if (action.type === REFETCH_LOGS_SUCCESS) {
					dispatch(actions.toggleSpinner());
				}
				return action;
			});
		},

		resetCalendar: () => ({
			type: RESET_CALENDAR
		}),

		resetPayoutsForm: () => dispatch => {
			dispatch(reset('affiliateProfilePayoutSettingsForm'));
		},

		resetIndTermsForm: () => dispatch => {
			dispatch(reset('affiliateIndividualTermsForm'));
			dispatch({
				type: RESET_IND_TERMS_FORM
			});
		},

		resetIndAlertsForm: () => dispatch => {
			dispatch(reset('affiliateIndividualAlertsForm'));
			dispatch({
				type: RESET_IND_ALERTS_FORM
			});
		},

		resetIndAlertsTermsForm: () => dispatch => {
			dispatch(reset('affiliateIndividualAlertsForm'));
			dispatch({
				type: RESET_IND_TERMS_FORM
			});
		},

		resetChildAffiliatesForm: () => dispatch => {
			dispatch(reset('childAffiliatesForm'));
			dispatch(destroy('affiliateProfileAffiliatesTabSearchForm'));
			dispatch(destroy('affiliateProfileAffiliateTabStatusesForm'));
			dispatch({
				type: RESET_CHILD_AFFS_FORM
			});
		},

		openCreateThreadFormWithAffiliate: () => (dispatch, getState) => {
			const { data } = getState().affiliateProfile;
			const affiliate = {
				value: data.id,
				label: data.email
			};
			dispatch(threadForm.actions.toggleCreateThreadForm(['affiliate_id']));
			dispatch(change('affiliateProfileCreateThreadForm', 'affiliate_id', [affiliate]));
		},

		openCreatePayoutFormWithAffiliate: () => (dispatch, getState) => {
			const { data } = getState().affiliateProfile;
			const affiliate = {
				value: data.id,
				label: data.email
			};
			dispatch(actions.fetchPayoutSystems()).then(action => {
				if (action.type === FETCH_PAYOUT_SYSTEMS_SUCCESS) {
					dispatch(payoutForm.actions.toggleCreatePayoutForm(['affiliate_id']));
					dispatch(change('affiliateProfileCreatePayoutForm', 'affiliate_id', [affiliate]));
					dispatch(payoutForm.actions.createPayoutFormGetAffiliateSettings([affiliate]));
				}
			});
		},

		openPointsTransactionFormWithAffiliate: () => (dispatch, getState) => {
			const { data } = getState().affiliateProfile;
			const affiliate = {
				value: data.id,
				label: data.email
			};
			dispatch(pointTransactionForm.actions.togglePointTransactionForm(['affiliate_id']));
			dispatch(change('affiliateProfilePointTransactionForm', 'affiliate_id', [affiliate]));
		},

		openIncreaseApproveForm: () => (dispatch, getState) => {
			dispatch(increaseApproveForm.actions.toggleIncreaseApproveForm());
		},
		openRequestGarantForm: () => (dispatch, getState) => {
			dispatch(requestGarantForm.actions.toggleIncreaseApproveForm());
		},
		openDailyApproveForm: () => (dispatch, getState) => {
			dispatch(increaseApproveForm.actions.toggleIncreaseApproveForm());
		},

		openTransferBalanceAndAccumulatedPointsForm: () => (dispatch, getState) => {
			const { data } = getState().affiliateProfile;
			const affiliate = {
				value: data.id,
				label: data.email
			};

			dispatch(transferBalanceAndAccumulatedPointsForm.actions.toggleForm());

			dispatch(change(
				'transferBalanceAndAccumulatedPointsForm',
				'sourceAffiliateId',
				[affiliate]
			));
			dispatch(change(
				'transferBalanceAndAccumulatedPointsForm',
				'comment',
				'Переброс квидов за конверсии в рамках задачи CU-XXXXXXX'
			));
		},
		submitTransferBalanceAndAccumulatedPointsForm: transferBalanceAndAccumulatedPointsForm.actions.submitForm,
		toggleTransferBalanceAndAccumulatedPointsForm: transferBalanceAndAccumulatedPointsForm.actions.toggleForm,

		openTransactionFormWithAffiliate: () => (dispatch, getState) => {
			const { data } = getState().affiliateProfile;
			const affiliate = {
				value: data.id,
				label: data.email
			};
			dispatch(transactionForm.actions.toggleTransactionForm(['affiliate_id']));
			dispatch(transactionForm.actions.transactionFormGetAffiliateSettings([affiliate]));
		},

		sessionsDelete: id => dispatch => {
			if (confirm(`Confirm sessions invalidation for affiliate id:${id}.`)) {
				dispatch(actions.toggleSpinner());
				return http({
					url: `${endpoints.AFFILIATES.SESSIONS_DELETE.replace('{id}', id)}`,
					method: 'put',
					actions: [SESSIONS_DELETE_SUCCESS, REQUEST_ERROR],

					data: {
						id: id
					}
				})(dispatch).then(action => {
					dispatch(actions.toggleSpinner());
					if (action.type === SESSIONS_DELETE_SUCCESS) {
						alert(`Number of deleted sessions: ${_pathOr(0, ['payload', 'data', 'count'])(action)}`);
					}
				});}
		},
		updateGeos: (geos) => (dispatch) => {
			dispatch({
				type: UPDATE_GEOS,
				geos
			});
		}
	},
	{
		individualTermsFiltersActions: individualTermsFilters.actions
	},
	threadForm.actions,
	payoutForm.actions,
	transactionForm.actions,
	addOfferFromAffiliateForm.actions,
	pointTransactionForm.actions,
	increaseApproveForm.actions,
	requestGarantForm.actions,
	approveDailyForm.actions,
	individualTermsForm.actions,
	individualAlertsForm.actions,
	calendar.actions,
	affiliateApproveIncreaseCalendar.actions,
	affiliateTransactionCalendar.actions,
	garantRequestCalendar.actions,
	dashboardVisitCalendar.actions,
	attachments.actions,
	childAffiliatesForm.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('affiliates/profile') !== -1) {
				return {
					...state
				};
			}
			return initialState;
		},
		[APPLY_FILTERS]: (state, action) => {
			return {
				...state,
				data: {
					...state.data,
					logs: action.payload
				}
			};
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const [affiliate, balances, logs, childAffiliates, campaignPayouts] =
			action.payload && action.payload.map(res => res.data);

			return {
				...state,
				data: { ...affiliate, balances, logs: logs.rows, campaignPayouts },
				logsPagination: {
					...state.logsPagination,
					total: logs.total
				},
				childAffiliatesAmount: childAffiliates.total,
				totalChildAffiliatesAmount: childAffiliates.total,
				affiliateId: action.meta,
				showProgressBar: false,
				error: false
			};
		},
		[SHOW_MORE_LOGS_SUCCESS]: (state, action) => ({
			...state,
			data: { ...state.data, logs: state.data.logs.concat(action.payload.data.rows) },
			logsPagination: {
				...state.logsPagination,
				currentPage: action.meta
			}
		}),
		[SHOW_MORE_NOTES_SUCCESS]: (state, action) => ({
			...state,
			notes: state.notes.concat(action.payload.data.rows),
			logsPagination: {
				...state.logsPagination,
				currentPage: action.meta
			}
		}),
		[FETCH_OFFERS_SUCCESS]: (state, action) => {
			const { affiliateId } = state;
			const offers = action.payload.data;
			const usersOffers = offers.filter(entity => entity.available);
			const usersOfferTerms = usersOffers
				.map(offer => {
					return (
						offer.payouts &&
						offer.payouts.map(payout => ({
							...payout,
							affiliate_id: affiliateId,
							offer: offer.title,
							offer_id: offer.id,
							status: payout.status
						}))
					);
				})
				.filter(Boolean);

			return {
				...state,
				offers: usersOffers,
				individualTerms: flatten(usersOfferTerms),
				showProgressBar: false
			};
		},
		[FETCH_MATRIX_SUCCESS]: (state, action) => {
			return {...state, payoutMatrix: action.payload.data, showProgressBar: false };
		},
		[FETCH_ALERTS_SUCCESS]: (state, action) => {
			return {
				...state,
				alerts: action.payload.data,
				showProgressBar: false
			};
		},
		[FETCH_CHILD_AFFILIATES_SUCCESS]: (state, action) => ({
			...state,
			childAffiliates:
				action.meta && action.meta.shouldAppendData
					? [].concat(state.childAffiliates, action.payload.data.rows)
					: action.payload.data.rows,
			childAffiliatesAmount: action.payload.data.total,
			showProgressBar: action.meta && action.meta.shouldShowProgressBar ? action.meta.shouldShowProgressBar : false
		}),
		[FETCH_PAYOUT_SETTINGS_SUCCESS]: (state, action) => {
			const [affiliatePayoutSettings, balances] = action.payload.data;
			return {
				...state,
				payoutSettings: affiliatePayoutSettings,
				balances,
				showProgressBar: false
			};
		},
		[MOVE_MONEY_SUCCESS]: (state, action) => {
			return {
				...state,
				data: {
					...state.data,
					balances: action.payload.data
				},
				showProgressBar: false
			};
		},
		[FETCH_PAYOUT_SYSTEMS_SUCCESS]: (state, action) => ({
			...state,
			payoutSystems: action.payload.data,
			showProgressBar: false
		}),
		[FETCH_DASHBOARD_SIGNUP_VISIT_DATA_SUCCESS]: (state, action) => ({
			...state,
			dashboardSignupVisitData: action.payload.data,
			showProgressBar: false
		}),
		[TOGGLE_AFFILIATE_STATUS]: (state, action) => ({
			...state,
			data: { ...state.data, status: action.meta },
			showProgressBar: false
		}),
		[TOGGLE_AFFILIATE_LEVEL]: (state, action) => ({
			...state,
			data: { ...state.data, level: action.meta },
			showProgressBar: false
		}),
		[CHANGE_TAB]: (state, action) => ({
			...state,
			currentTab: action.payload
		}),
		[SHOW_COMMENT_SUBMIT_BTN]: state => ({
			...state,
			showCommentSubmitBtn: true
		}),
		[REFETCH_LOGS_SUCCESS]: (state, action) => ({
			...state,
			data: { ...state.data, logs: action.payload.data.rows },
			logsPagination: {
				...state.logsPagination,
				currentPage: 1,
				total: action.payload.data.total
			},
			showCommentSubmitBtn: false
		}),
		[RESET_CALENDAR]: state => ({
			...state,
			calendar: calendar.initialState
		}),
		[RESET_IND_TERMS_FORM]: state => ({
			...state,
			individualTermsForm: individualTermsForm.initialState
		}),
		[FETCH_NOTES_SUCCESS]: (state, action) => ({
			...state,
			notes: action.payload.data.rows,
			notesPagination: {
				...state.notesPagination,
				currentPage: 1,
				total: action.payload.data.total
			}
		}),
		[RESET_IND_ALERTS_FORM]: state => ({
			...state,
			individualAlertsForm: individualAlertsForm.initialState
		}),
		[RESET_CHILD_AFFS_FORM]: state => ({
			...state,
			childAffiliatesForm: childAffiliatesForm.initialState
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[MOVE_MONEY_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[REQUEST_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[UPDATE_GEOS]: (state, action) => ({
			...state,
			data: {
				...state.data, geos: action.geos
			}
		})
	},
	threadForm.reducers,
	payoutForm.reducers,
	transactionForm.reducers,
	addOfferFromAffiliateForm.reducers,
	transferBalanceAndAccumulatedPointsForm.reducers,
	pointTransactionForm.reducers,
	individualTermsForm.reducers,
	individualTermsFilters.reducers,
	individualAlertsForm.reducers,
	increaseApproveForm.reducers,
	requestGarantForm.reducers,
	calendar.reducers,
	affiliateApproveIncreaseCalendar.reducers,
	affiliateTransactionCalendar.reducers,
	garantRequestCalendar.reducers,
	dashboardVisitCalendar.reducers,
	dashboardVisitActionsSwitch.reducers,
	attachments.reducers,
	approveDailyForm.reducers,
	childAffiliatesForm.reducers,
	dashboardVisits.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.affiliateProfile.data,
	getPayoutMatrix: state => state.affiliateProfile.payoutMatrix,
	getOffers: state => state.affiliateProfile.offers,
	getCampaignPayouts: state => state.affiliateProfile.data && state.affiliateProfile.data.campaignPayouts,
	getAffiliateID: state => Number(state.affiliateProfile.affiliateId),
	getLogs: state => ({
		rows: state.affiliateProfile.data && state.affiliateProfile.data.logs,
		total: state.affiliateProfile.logsPagination.total,
		limit: state.affiliateProfile.logsPagination.perPage
	}),
	getNotes: state => ({
		rows: state.affiliateProfile.notes,
		total: state.affiliateProfile.notesPagination.total,
		limit: state.affiliateProfile.notesPagination.perPage
	}),
	getAffiliateFilterQuery: state => {
		const affiliate = state.affiliateProfile.data ? state.affiliateProfile.data : {};

		const query = {
			filters: { affiliates: [affiliate.id] }
		};

		const entity = {
			filters: [
				{
					name: 'affiliates',
					values: [{ value: affiliate.id, label: affiliate.email }]
				}
			]
		};

		return qs.stringify({
			query,
			entity
		});
	},
	getSearchInitialValue: state => ({ search: state.affiliateProfile.search }),
	getParamsInitialValues: state => ({ ...state.affiliateProfile.params }),
	getNewsTypesList: state => state.root.lists['news-types'],
	getNotificationEventTypesList: state => state.root.lists['notification-event-types'],
	getPayoutCurrencies: state => state.root.lists['payout-currencies'].map(curr => curr.id),
	getCurrentTab: state => state.affiliateProfile.currentTab,
	getCommentSubmitBtn: state => state.affiliateProfile.showCommentSubmitBtn,
	getCalendar: state => calendar.selectors.getCalendar(state => state.affiliateProfile.calendar, state => state)(state),
	affiliateApproveIncreaseCalendar: state => affiliateApproveIncreaseCalendar.selectors.getCalendar(
		state => state.affiliateProfile.affiliateApproveIncreaseCalendar, state => state
	)(state),
	getAffiliateTransactionCalendar: state => affiliateTransactionCalendar.selectors.getCalendar(
		state => state.affiliateProfile.affiliateTransactionCalendar, state => state
	)(state),
	getGarantRequestCalendar: state => garantRequestCalendar.selectors.getCalendar(
		state => state.affiliateProfile.garantRequestCalendar, state => state
	)(state),
	getPrioritiesList: threadForm.selectors.getPrioritiesList,

	getIsAddOfferFromAffiliateFormVisible:
			state => addOfferFromAffiliateForm.selectors.getIsAddOfferFromAffiliateFormVisible(state),

	getShouldShowCreateThreadFormStatus: threadForm.selectors.getShouldShowCreateThreadFormStatus,
	getAttachmentsInfo: threadForm.selectors.getAttachmentsInfo,
	getCreateThreadFormInitialValues: threadForm.selectors.getInitialValues,
	getCreateThreadFormDisabledFields: threadForm.selectors.getDisabledFields,

	getCreatePayoutFormData: payoutForm.selectors.getFormData,

	getApproveDailyFormData: approveDailyForm.selectors.getFormData,
	getApproveDailyFormInitialData: approveDailyForm.selectors.getFormInitialData,
	getIsApproveDailyFormVisible: approveDailyForm.selectors.getIsApproveDailyFormVisible,
	getDailyApprovesList: approveDailyForm.selectors.getDailyApprovesList,
	getCreatePayoutShouldShowStatus: payoutForm.selectors.getShouldShowStatus,

	getTransactionFormData: transactionForm.selectors.getFormData,
	getTransactionShouldShowStatus: transactionForm.selectors.getShouldShowStatus,
	getTransactionFormType: transactionForm.selectors.getFormType,
	getTransactionFormExchangeRate: transactionForm.selectors.getFormExchangeRate,

	getTransferBalanceFormData: transferBalanceAndAccumulatedPointsForm.selectors.getFormData,
	getTransferBalanceShouldShowStatus: transferBalanceAndAccumulatedPointsForm.selectors.getShouldShowStatus,

	getPointTransactionFormData: pointTransactionForm.selectors.getPointFormData,
	getPointTransactionShouldShowStatus: pointTransactionForm.selectors.getShouldShowPointStatus,

	getIndividualTermsFormData: individualTermsForm.selectors.getFormData,
	getIndividualTermsFilters: state => individualTermsFilters.selectors.getFilters(
		state => state.affiliateProfile.individualTermsFilters,
		state => state
	)(state),
	getIndividualAlertsFormData: individualAlertsForm.selectors.getFormData,

	getIncreaseApproveFormData: increaseApproveForm.selectors.getFormData,
	getRequestGarantFormData: requestGarantForm.selectors.getFormData,
	getAvailableCurrencies: increaseApproveForm.selectors.getAvailableCurrencies,
	getIncreaseApproveFormVisibility: increaseApproveForm.selectors.getIncreaseApproveFormVisibility,
	getRequestGarantFormVisibility: requestGarantForm.selectors.getRequestGarantFormVisibility,
	getRequestGarantFormOfferApproveData: requestGarantForm.selectors.getRequestGarantFormOfferApproveData,
	getIsBlockedSubmitButton: increaseApproveForm.selectors.getIsBlockedSubmitButton,
	getIsBlockedRequestGarantSubmitButton: requestGarantForm.selectors.getIsBlockedSubmitButton,
	getIncreaseApproveFormOfferApproveData: increaseApproveForm.selectors.getIncreaseApproveFormOfferApproveData,
	getIncreaseApproveFormCalendar: increaseApproveForm.selectors.getIncreaseApproveFormCalendar,
	getAttachments: attachments.selectors.getAttachments,

	getDashboardSignupVisitData: state => state.affiliateProfile.dashboardSignupVisitData,
	getDashboardVisits: state => state.affiliateProfile.dashboardVisits,
	getDashboardVisitActionsSwitchItems: state => dashboardVisitActionsSwitch.selectors.getControls(state),
	getDashboardVisitCalendar: state => dashboardVisitCalendar.selectors.getCalendar(
		state => state.affiliateProfile.dashboardVisitCalendar,
		state => state
	)(state),

	getChildAffiliates: state => state.affiliateProfile.childAffiliates,
	getSelectedCurrenciesPanIndexMap: state => state.affiliateProfile.individualTermsForm.selectedCurrenciesPanIndexMap,
	getChildAffiliatesAmount: state => state.affiliateProfile.childAffiliatesAmount,
	getTotalChildAffiliatesAmount: state => {
		return state.affiliateProfile.childAffiliatesAmount;
	},
	getChildAffiliatesFormData: childAffiliatesForm.selectors.getFormData,

	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getProgressBar: state => state.affiliateProfile.showProgressBar,
	getError: state => state.affiliateProfile.error
};

const request = (offset, limit, props, dispatch, getState) => {
	const { calendar } = getState().affiliateProfile;
	const query = qs.stringify({
		offset,
		limit,
		date_range: getDateRangeFromCalendar(calendar)
	});

	return http({
		url: [
			`${endpoints.AFFILIATES.BASE}/${props.params.id}`,
			`${endpoints.AFFILIATES.BASE}/${props.params.id}/balances`,
			`${endpoints.AFFILIATES.LOGS.replace('{id}', props.params.id)}?${query}`,
			`${endpoints.AFFILIATES.MASTER_ACCOUNT}/${props.params.id}`,
			`${endpoints.CAMPAIGNS.PAYOUTS}?${qs.stringify({affiliate_id: props.params.id})}`
		],
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		addToResponse: props.params.id
	})(dispatch)
		.then(action => {
			if (action.type === FETCH_DATA_SUCCESS) {
				const description = action.payload[0].data.description;
				dispatch(change('affiliateProfileCommentForm', 'comment', description));
				if (selectors.getCan(getState())('conversionsApproveDaily'))
					return dispatch(approveDailyForm.actions.fetchApproveDailyList());
			}
		});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/profile/index.js